import { useToggle } from '@mantine/hooks';
import { ICustomField } from '@models/CustomField.model';
import { INVENTORY_KEY_PREFIX } from '@pages/Products/Products';
import { EquipmentsMap } from '@store/machines/equipmentsPicker.schema';
import { massEdit } from '@store/machines/massEdit';
import { RootState, useAppDispatch } from '@store/redux/store';
import { useMachine } from '@xstate/react';
import { addKnownFromEquipments } from '@store/redux/features/inventory/knownData';
import { memo, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { isNonNullable } from '@utils/isNonNullable';
import extend from 'just-extend';
import { useToast } from '@hooks/index';
import AppError from '@utils/AppError';
import { LoadMoreComponent } from '@components/LoadMoreComponent/LoadMoreComponent';
import {
  LoadMoreContext,
  useVirtualPages,
} from '@components/LoadMoreComponent/LoadMoreContext';
import { CloseDialog } from './CloseDialog';
import { GroupEditWarn } from './GroupEditWarn';
import { getInputProps, reset, resetGlobal } from './inputs/utils';
import { InputGrid } from './styles';
import {
  IFormDataOnboardingModal,
  OnboardingModalProps,
} from '../OnBoardingModal';
import {
  BlueButton,
  Buttons,
  Right,
  SingleInputs,
  WhiteButton,
} from '../OnBoardingModal.styles';
import {
  getDefaultGlobalValues,
  getDefaultGlobalViewValues,
  getDefaultValues,
  getDefaultViewValues,
  NeededFromEq,
} from '../OnBoardingModal.utils';
import {
  patchEquipmentArrayLambdaWrap,
  patchEquipmentArrayMassEdit,
} from '../helpers/PatchMassEditLambda';
import type {
  OnboardingControls,
  QueriesToFetch,
  ViewValues,
  GlobalInput,
} from '../types';
import EquipmentView from './EquipmentView';
import { filterGlobalDirty } from '../helpers/filterDirty';
import { filterDirtySingle } from '../helpers/filterDirtySingle';
import { getInputs } from '../helpers/getInputs';
import { getPromptItems, getSavingItems } from '../helpers/getPromptItems';
import { getTagsDiff } from '../helpers/getTagsDiff';
import { IHeadingChildren } from '../helpers/HeadingsCalc';
import { doTrackAssetBulkEdit } from '../helpers/doTrackAssetBulkEdit';
import RightHeading from './RightHeading';

export type EditModes =
  | 'GROUP-OLD'
  | 'GROUP-LAMBDA-FILTER'
  | 'SINGLE'
  | 'GROUP-LAMBDA-ALL';
export interface RightSideProps {
  defaultEquipments: EquipmentsMap;
  customFields: ICustomField[];
  activePage: IHeadingChildren;
  isOnboarding: boolean;
  onDone: OnboardingModalProps['onDone'];
  controlRef: React.MutableRefObject<OnboardingControls | undefined>;
  fetchedQueries: QueriesToFetch[];
  mode: EditModes;
  queryParams: OnboardingModalProps['queryParams'];
  deselectedItems: string[];
}

function RightSide(props: React.PropsWithChildren<RightSideProps>) {
  const {
    defaultEquipments,
    customFields,
    activePage,
    onDone,
    isOnboarding,
    controlRef,
    mode,
    fetchedQueries,
    queryParams,
    deselectedItems,
  } = props;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [closeModal, toggleCloseModal] = useToggle();
  const [savingPrompt, toggleSavingPrompt] = useToggle();

  // State
  const [state, send] = useMachine(massEdit, {
    context: {
      equipments: defaultEquipments,
    },
    actions: {
      onClose(context, event) {
        onDone(context, event);
      },
      toggleModal(_, event) {
        if (event.isSaving) {
          toggleSavingPrompt();
        } else {
          toggleCloseModal();
        }
      },
    },
    services: {
      doSave: () => () => {
        doSaveIfDirty();
      },
    },
  });
  const { equipments } = state.context;
  const onlyGroupEdits = mode.startsWith('GROUP');
  const dispatch = useAppDispatch();
  const studioId = useSelector((_state: RootState) => _state.auth.studioId);
  const knownData = useSelector((_state: RootState) => _state.knownData);
  // Mutations.
  const { mutate } = useMutation(patchEquipmentArrayMassEdit, {
    onSuccess: (_, variables) => {
      queryClient.resetQueries(INVENTORY_KEY_PREFIX);
      send({
        type: 'SUCCESS',
        values: variables.params,
        knownData,
      });

      const dirtyEqs = variables.dirtyFields.equipments;
      doTrackAssetBulkEdit(dirtyEqs, variables);
    },
    onError: (err, variables) => {
      onError(variables.dirtyFields);
      send({ type: 'ERROR' });
      if (err instanceof AppError) {
        toast.error(err.message);
      }
    },
  });

  const { mutate: mutateLambda } = useMutation(patchEquipmentArrayLambdaWrap, {
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.resetQueries(INVENTORY_KEY_PREFIX);
      send({
        type: 'SUCCESS',
        values: variables.equipment,
        knownData,
        tagsDiff: variables.tagsDiff,
      });
    },
    onError: async (err, variables) => {
      onError(variables.dirtyFields);
      onGroupError(variables.dirtyGlobalFields);
      send({ type: 'ERROR' });
      if (err instanceof AppError) {
        toast.error(err.message);
      }
    },
  });

  // Forms
  const defaultViewValues = {
    views: getDefaultViewValues(equipments),
  };

  const defaultGlobalViewValues = getDefaultGlobalViewValues();

  const [defaultRun, defaultGlobalValues] = useMemo(
    () => [
      getDefaultValues(equipments, customFields),
      getDefaultGlobalValues(customFields, equipments),
    ],
    [customFields, equipments],
  );
  const { equips: defaultValues, known } = defaultRun;
  const [formV, setFormV] = useState<IFormDataOnboardingModal>(() => {
    return {
      equipments: defaultValues,
      dirtyFields: {},
      isDirty: false,
    };
  });
  const [globalFormV, setGlobalFormV] =
    useState<GlobalInput>(defaultGlobalValues);

  const viewForm = useForm<ViewValues>({
    defaultValues: defaultViewValues,
  });

  const globalViewForm = useForm({
    defaultValues: defaultGlobalViewValues,
  });

  const { isDirty, equipments: values, dirtyFields } = formV;
  const { dirtyGlobalFields } = globalFormV;
  const { equipment: dEquipment } = defaultGlobalValues;

  // Effects
  useEffect(() => {
    send({ type: 'DIRTY', isDirty });
  }, [isDirty]);

  // Once customFields are available,silently set value
  useEffect(() => {
    if (fetchedQueries.includes('custom-fields')) {
      setFormV(prev => {
        const newValues = reset(defaultValues, prev.equipments, dirtyFields);
        return { ...prev, equipments: newValues };
      });

      setGlobalFormV(prev => {
        const newGlobal = resetGlobal(
          dEquipment,
          prev.equipment,
          dirtyGlobalFields,
        );
        return { ...prev, equipment: newGlobal };
      });
    }
  }, [fetchedQueries]);
  useEffect(() => {
    // without the setTimeout, this will cause the mass edit modal animation to replay
    setTimeout(() => dispatch(addKnownFromEquipments(known)), 0);
  }, []);
  // Misc.
  let loadMoreOpts = {};
  if (onlyGroupEdits) {
    loadMoreOpts = {
      fetchNextPage: () => {},
      hasNextPage: false,
      loadMoreText: 'Pagination disabled. Only previewing first 20 items',
    };
  }
  const { loadMoreCtx, slice } = useVirtualPages(
    values,
    undefined,
    loadMoreOpts,
  );

  if (!activePage) return null;

  const rows = slice.map((field, index) => {
    const equipment = equipments.get(field.id) as NeededFromEq;

    const inputs = getInputs({
      activePage,
      setFormV,
      mode,
      viewForm,
      customFieldsQuery: customFields,
      setGlobalFormV,
      equipmentsCount: values.length,
      config: {
        kind: 'SINGLE',
        formV,
        index,
        equipment,
        onlyGroupEdits: mode.startsWith('GROUP'),
      },
    });
    const _props = getInputProps(['serial', 'productId'], formV, index);

    return (
      <InputGrid key={field.id}>
        <EquipmentView {..._props} groupEdits={onlyGroupEdits} />
        {inputs}
      </InputGrid>
    );
  });
  return (
    <Right>
      <RightHeading
        isDirty={formV.isDirty}
        setFormV={setFormV}
        globalFormV={globalFormV}
        setGlobalFormV={setGlobalFormV}
        viewForm={viewForm}
        globalViewForm={globalViewForm}
        send={send}
        actionQueue={state.context.actionQueue}
        matches={state.matches}
        activePage={activePage}
        isOnboarding={isOnboarding}
        customFields={customFields}
        onSubmit={() => {
          send({ type: 'SAVE' });
        }}
        equipmentsCount={values.length}
        mode={mode}
      />
      <SingleInputs className="bscroll" data-fieldtype={activePage.inputType}>
        <LoadMoreContext.Provider value={loadMoreCtx}>
          {onlyGroupEdits && <GroupEditWarn length={equipments.size} />}
          {rows}
          <LoadMoreComponent />
        </LoadMoreContext.Provider>
      </SingleInputs>

      <Buttons>
        {activePage.prevId && (
          <WhiteButton
            onClick={() => {
              if (!controlRef.current) return;
              controlRef.current.prevPage();
            }}
          >
            Return
          </WhiteButton>
        )}
        <BlueButton
          onClick={() => {
            if (!controlRef.current) return;
            if (activePage.nextId) {
              send({ type: 'SAVE' });
              controlRef.current.nextPage();
            } else if (isDirty) {
              send({ type: 'SAVE-THEN-CLOSE', reason: null });
            } else {
              send({
                type: 'TERMINATE',
                reason: null,
                isSaving: state.matches('open.editing.saving'),
              });
            }
          }}
        >
          {activePage.nextId ? 'Next' : 'Save'}
        </BlueButton>
      </Buttons>
      <CloseDialog
        isOpen={closeModal}
        itemList={getPromptItems(state.context.terminateReason, send)}
        onClose={() => {
          toggleCloseModal(false);
        }}
        onClickAway={() => {
          send({ type: 'ABORT' });
        }}
      />
      <CloseDialog
        isOpen={savingPrompt}
        itemList={getSavingItems(state.context.terminateReason, send)}
        onClose={() => {
          toggleSavingPrompt(false);
        }}
        onClickAway={() => {
          send({ type: 'ABORT' });
        }}
      />
    </Right>
  );

  // Fn defs.
  function onError(oldDirty: IFormDataOnboardingModal['dirtyFields']) {
    // Set the new dirtyFields
    setFormV(previousFormV => {
      const mergedDirty = extend(true, oldDirty, previousFormV.dirtyFields);
      return { ...previousFormV, dirtyFields: mergedDirty, isDirty: true };
    });
  }
  function onGroupError(oldDirty: GlobalInput['dirtyGlobalFields']) {
    setGlobalFormV(previousValue => {
      const { dirtyGlobalFields: newGlobalDirty } = previousValue;
      const mergedDirty = extend(true, oldDirty, newGlobalDirty);
      return {
        ...previousValue,
        dirtyGlobalFields: mergedDirty,
        isDirty: true,
      };
    });
  }
  function doSaveIfDirty() {
    // Initialise.

    // Send out.
    if (mode === 'GROUP-OLD' || mode === 'SINGLE') {
      const equipmentsFiltered = values
        .map((equipment, index) => {
          return filterDirtySingle(
            equipment,
            dirtyFields?.equipments?.at(index),
          );
        })
        .filter(isNonNullable);

      mutate({
        params: equipmentsFiltered,
        dirtyFields,
      });
    } else {
      const { equipment } = globalFormV;

      const anyDirty = dirtyGlobalFields.equipment;
      const objecttosend = filterGlobalDirty(equipment, anyDirty);
      const { tagsId } = objecttosend;
      const { tagsId: oldTagsId } = defaultGlobalValues.equipment;

      const tagsDiff = tagsId && getTagsDiff(tagsId, oldTagsId, values.length);

      mutateLambda({
        equipment: objecttosend,
        queryParams,
        dirtyFields,
        dirtyGlobalFields,
        studioId,
        tagsDiff,
        deselectedItems,
      });
    }
    // Reset affter.

    Promise.resolve().then(async () => {
      await Promise.resolve();
      setFormV(previous => {
        return { ...previous, dirtyFields: {}, isDirty: false };
      });
      setGlobalFormV(previous => {
        return { ...previous, dirtyGlobalFields: {}, isDirty: false };
      });
    });
  }
}
export default memo(RightSide);
